import React, { useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import CustomSvg from "../svgs/CustomSvg";
import "./css/payment.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../utils/hooks";
import { postRequest } from "../apiRequests/requestApi";
import CustomErrorMsg from "../errorMsg/CustomErrorMsg";


// Custom Success Modal for Withdrawals
function WithdrawSuccessModal({ visible, onClose, subTxt }) {
  const [secondsLeft, setSecondsLeft] = React.useState(300); // 5 minutes
  React.useEffect(() => {
    if (!visible) return;
    setSecondsLeft(300); // reset timer when opened
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          if (onClose) onClose();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [visible, onClose]);

  if (!visible) return null;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return (
    <Modal show={visible} onHide={onClose} centered size="md">
      <div className="p-6 text-center bg-[#181c24] rounded-lg">
        <div className="flex justify-center mb-4">
          <CustomSvg name="check-circle" style={{ width: 60, height: 60 }} />
        </div>
        <h4 className="text-white font-bold text-2xl mb-2">Success!</h4>
        <p className="text-gray-200 mb-6">{subTxt || "Withdrawal request submitted successfully!"}</p>
        <div className="mb-4 text-gray-400 text-sm">Check in {minutes}:{seconds.toString().padStart(2, '0')}</div>
        <button
          className="w-full bg-pink-600 hover:bg-pink-700 transition text-white font-poppins font-semibold py-3 rounded-lg shadow-md mt-2"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

export default function WithdrawModal({ modalProps }) {
  // const { user } = useUser();
  const navigate = useNavigate();
  
  const navigateTo = (path) => navigate(path);
  const { user } = useUser();
    React.useEffect(() => {
    if (modalProps?.visible && user && (!user.account_name || !user.account_number || !user.bank_name)) {
      navigate('/user-profile/add-bank');
    }
    // Prefill amount from modalProps.amount if present
    if (modalProps?.visible && modalProps.amount) {
      setFormData(prev => ({
        ...prev,
        amount: (modalProps.amount).toString()
      }));
    }
  }, [modalProps?.visible, user, modalProps?.amount]);
  // console.log("User in WithdrawModal:", user);

  // Pre-fill bank/account details from user profile
  const [formData, setFormData] = useState({
    amount: "",
    password: "",
    accountName: user?.account_name || "",
    accountNumber: user?.account_number || "",
    bankName: user?.bank_name || "",
    description: ""
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    onHide: null,
    size: "md",
  });
  const [fetchingAccount, setFetchingAccount] = useState(false);

  const togglePasswordVisibility = () => setPasswordVisible((prev) => !prev);

  const openSuccessModal = () => {
    if (modalProps && typeof modalProps.onHide === 'function') {
      modalProps.onHide(); // Close withdrawal modal first
    }
    setSuccessModal({ visible: true, onHide: hideSuccessModal, size: "md" });
  };
  const hideSuccessModal = () =>
    setSuccessModal({ visible: false, onHide: null, size: "md" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setLoading(true);
      // Validate amount
      const amountInKobo = parseFloat(formData.amount) * 100;
      if (amountInKobo < 200000) {
        throw new Error("Minimum withdrawal amount is ₦2,000");
      }
      if (amountInKobo > user.walletBalance) {
        throw new Error("Insufficient balance");
      }
      if (!formData.accountNumber || formData.accountNumber.length !== 10) {
        throw new Error("Please enter a valid 10-digit account number");
      }
      if (!formData.bankName) {
        throw new Error("Please select a bank");
      }
      if (!formData.password) {
        throw new Error("Please enter your password");
      }
      if (!formData.accountName) {
        throw new Error("Account name could not be resolved");
      }
      const response = await postRequest({
        url: "payment/manual-withdraw",
        data: {
          amount: amountInKobo,
          accountNumber: formData.accountNumber,
          accountName: formData.accountName,
          description: formData.description,
          password: formData.password,
        },
      });
      if (!response.success) {
        throw new Error(response.message || "Withdrawal failed");
      }
      openSuccessModal();
    } catch (error) {
      setError(error.message || "Failed to process withdrawal");
    } finally {
      setLoading(false);
    }
  };

  if (modalProps) {
    const { size, onHide, visible } = modalProps;

    const goHome = () => {
      hideSuccessModal();
      onHide();
      navigateTo("/");
    };

    return (
      <>
        <Modal show={visible} size={size} onHide={onHide} centered={true}>
          <div className="p-1 mx-3">
            <div
              onClick={onHide}
              className="flex items-center justify-end mb-4 cursor-pointer"
            >
              <CustomSvg name="x" />
            </div>

            <div className="bg-[#181c24] rounded-[18px] shadow-lg p-8 pb-6 mb-4 max-w-[480px] mx-auto">
              <h4 className="mb-4 text-center font-quantico font-bold text-2xl text-white">
                Withdraw Money
              </h4>
              <div className="bg-[#23283a] rounded-lg p-4 mb-4">
                <p className="mb-2 font-medium text-sm text-gray-200 text-center font-poppins">
                  Available Balance
                </p>
                <h4 className="m-0 text-white font-bold font-poppins text-center">
                  ₦{(user.walletBalance / 100).toLocaleString()}
                </h4>
              </div>
              {error && (
                <div className="mb-3">
                  <CustomErrorMsg errorMsg={error} />
                </div>
              )}
              <form className="space-y-4" onSubmit={handleWithdraw}>
                <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <CustomSvg name="money" />
                  <input
                    type="number"
                    name="amount"
                    value={(formData.amount)}
                    onChange={handleInputChange}
                    placeholder="Amount (minimum ₦2,000)"
                    className="bg-transparent border-0 outline-none text-white ml-3 w-full font-quantico"
                  />
                </div>
               <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    readOnly
                    placeholder="Bank Name"
                    className="bg-transparent border-0 outline-none text-white w-full font-quantico opacity-70 cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    readOnly
                    placeholder="Account Number"
                    className="bg-transparent border-0 outline-none text-white w-full font-quantico opacity-70 cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <input
                    type="text"
                    name="accountName"
                    value={formData.accountName}
                    readOnly
                    placeholder="Account Name"
                    className="bg-transparent border-0 outline-none text-white w-full font-quantico opacity-70 cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description (optional)"
                    className="bg-transparent border-0 outline-none text-white w-full font-quantico"
                  />
                </div>
                <div className="flex items-center bg-[#23283a] rounded-lg px-3 py-2">
                  <CustomSvg name="lock" color="#fff" />
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="bg-transparent border-0 outline-none text-white ml-3 w-full font-quantico"
                  />
                  <span className="ml-2 cursor-pointer" onClick={togglePasswordVisibility}>
                    {passwordVisible ? (
                      <FiEye size={24} color="#fff" />
                    ) : (
                      <FiEyeOff size={24} color="#fff" />
                    )}
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-pink-600 hover:bg-pink-700 transition text-white font-poppins font-semibold py-3 rounded-lg shadow-md disabled:opacity-60 flex items-center justify-center mt-2"
                >
                  {loading ? (
                    <Spinner size="sm" variant="light" />
                  ) : (
                    <>
                      <span>Withdraw</span>
                      <span className="ml-2 flex items-center">
                        <CustomSvg name="arrow-right" />
                      </span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </Modal>
        <WithdrawSuccessModal
          visible={successModal.visible}
          onClose={hideSuccessModal}
          subTxt="Withdrawal request submitted successfully!"
        />
      </>
    );
  }

  return null;
}
